import { useEffect, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { ChevronDown, PanelLeftClose, PanelLeftOpen } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { Logo } from '@/components/common/Logo';
import { ProfileMenu } from '@/components/common/ProfileMenu';
import { useIsAdmin } from '@/api/hooks/useAuth';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';
import { navItems } from './navItems';
import { NewProjectButton } from './NewProjectButton';

const COLLAPSED_KEY = 'dashboard.sidebar.collapsed';

const topItemClasses = (active: boolean, collapsed: boolean) =>
  cn(
    'group flex items-center gap-3 rounded-md text-sm font-medium transition-colors',
    collapsed ? 'h-10 w-10 justify-center px-0' : 'px-3 py-2',
    active
      ? 'bg-sidebar-primary/10 text-sidebar-primary'
      : 'text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
  );

const childItemClasses = (active: boolean) =>
  cn(
    'flex items-center gap-2.5 rounded-md px-3 py-1.5 text-sm transition-colors',
    active
      ? 'bg-sidebar-primary/10 text-sidebar-primary font-medium'
      : 'text-sidebar-foreground/70 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
  );

export function DashboardSidebar() {
  const { t } = useTranslation('common');
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const location = useLocation();

  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(COLLAPSED_KEY) === '1';
    } catch {
      return false;
    }
  });

  useEffect(() => {
    try {
      window.localStorage.setItem(COLLAPSED_KEY, collapsed ? '1' : '0');
    } catch {
      /* storage blocked */
    }
  }, [collapsed]);

  const visibleItems = navItems.filter((item) => !item.adminOnly || isAdmin);

  return (
    <TooltipProvider delayDuration={200}>
      <aside
        className={cn(
          'hidden h-full shrink-0 flex-col border-r border-sidebar-border bg-sidebar text-sidebar-foreground transition-[width] duration-200 md:flex',
          collapsed ? 'w-16' : 'w-64',
        )}
      >
        {/* Logo + collapse toggle. When expanded the logo is wrapped in a
            flex-1 link so it centers horizontally between the left edge
            and the toggle button; clicking it returns to the home page. */}
        <div
          className={cn(
            'flex h-16 items-center gap-1 border-b border-sidebar-border/60',
            collapsed ? 'justify-center px-0' : 'pl-3 pr-2',
          )}
        >
          {!collapsed && (
            <Link
              to="/dashboard/projects"
              aria-label={t('nav.projects')}
              className="flex flex-1 justify-center rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
            >
              <Logo size="lg" />
            </Link>
          )}
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={() => setCollapsed((v) => !v)}
                aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
                className="flex h-8 w-8 items-center justify-center rounded-md text-sidebar-foreground/70 transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
              >
                {collapsed ? (
                  <PanelLeftOpen className="h-4 w-4" />
                ) : (
                  <PanelLeftClose className="h-4 w-4" />
                )}
              </button>
            </TooltipTrigger>
            {collapsed && (
              <TooltipContent side="right" sideOffset={8}>
                {t('nav.expand')}
              </TooltipContent>
            )}
          </Tooltip>
        </div>

        <div className={cn('pt-4', collapsed ? 'flex justify-center px-2' : 'px-3')}>
          <NewProjectButton collapsed={collapsed} />
        </div>

        <nav
          className={cn(
            'flex-1 py-4',
            collapsed
              ? 'flex flex-col items-center gap-3 px-2'
              : 'space-y-1 px-3',
          )}
        >
          {visibleItems.map((item) => {
            const isSection = !!item.children;
            const isSectionActive = isSection && location.pathname.startsWith(item.to);

            if (isSection) {
              if (collapsed) {
                // Collapsed: show just the section icon as a button that
                // expands the sidebar and routes into the section.
                return (
                  <Tooltip key={item.to}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => {
                          setCollapsed(false);
                          navigate(item.to);
                        }}
                        className={cn(topItemClasses(isSectionActive, true))}
                        aria-label={t(item.labelKey)}
                      >
                        <item.icon className="h-4 w-4" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent side="right" sideOffset={8}>
                      {t(item.labelKey)}
                    </TooltipContent>
                  </Tooltip>
                );
              }
              return (
                <div key={item.to} className="space-y-0.5">
                  <button
                    type="button"
                    onClick={() => navigate(item.to)}
                    className={cn(topItemClasses(isSectionActive, false), 'w-full text-left')}
                  >
                    <item.icon className="h-4 w-4" />
                    <span className="flex-1">{t(item.labelKey)}</span>
                    <ChevronDown
                      className={cn(
                        'h-3.5 w-3.5 transition-transform',
                        isSectionActive ? 'rotate-0 opacity-80' : '-rotate-90 opacity-50',
                      )}
                    />
                  </button>
                  {isSectionActive && (
                    <div className="ml-3 space-y-0.5 border-l border-sidebar-border/60 pl-3">
                      {item.children!.map((child) => (
                        <NavLink
                          key={child.to}
                          to={child.to}
                          className={({ isActive }) => childItemClasses(isActive)}
                        >
                          <child.icon className="h-3.5 w-3.5" />
                          <span>{t(child.labelKey)}</span>
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              );
            }

            const linkEl = (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) => topItemClasses(isActive, collapsed)}
                aria-label={collapsed ? t(item.labelKey) : undefined}
              >
                <item.icon className="h-4 w-4" />
                {!collapsed && <span className="flex-1">{t(item.labelKey)}</span>}
              </NavLink>
            );

            if (collapsed) {
              return (
                <Tooltip key={item.to}>
                  <TooltipTrigger asChild>{linkEl}</TooltipTrigger>
                  <TooltipContent side="right" sideOffset={8}>
                    {t(item.labelKey)}
                  </TooltipContent>
                </Tooltip>
              );
            }
            return linkEl;
          })}
        </nav>

        <div
          className={cn(
            'border-t border-sidebar-border',
            collapsed ? 'flex justify-center px-2 py-3' : 'px-2 py-3',
          )}
        >
          <ProfileMenu collapsed={collapsed} />
        </div>
      </aside>
    </TooltipProvider>
  );
}
